import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { readConsent, subscribeConsent, writeConsent } from '@/lib/cookieConsent';

/**
 * LE BANDEAU COOKIES — « Cookie pop up : to implement » (Notion, « Last push
 * before going live » → Landing).
 *
 * FORME. Une carte en coin, pas un rideau plein écran : c'est la référence que
 * le client a jointe (une doc Mintlify, capture « Layout » de la page
 * Resources) et c'est aussi le seul placement qui ne bloque pas la lecture.
 * Trois actions, toutes au même niveau visuel côté refus/acceptation —
 * « Tout refuser » n'est pas relégué en lien discret, ce qui est précisément la
 * pratique que la CNIL sanctionne.
 *
 * EN BAS À DROITE, ET PAS À GAUCHE COMME LA RÉFÉRENCE. La doc Mintlify pose sa
 * carte en bas à gauche parce que ce site n'a rien à cet endroit. Notre
 * application, si : le rail porte le bouton du menu compte dans son pied, en
 * bas à gauche exactement. Une carte `fixed` posée là le RECOUVRE — elle
 * intercepte les clics — et l'opérateur ne peut plus atteindre ses réglages ni
 * sa langue tant qu'il n'a pas répondu au bandeau. C'est l'E2E qui l'a attrapé
 * (`language-switching`, « button-avatar-menu … cookie-banner intercepts
 * pointer events »), et c'était un vrai défaut, pas une gêne de test.
 *
 * Le coin bas-droit est libre : le lanceur Intercom l'occuperait, mais il ne
 * démarre QU'APRÈS le consentement, c'est-à-dire au moment précis où ce
 * bandeau disparaît. Les deux ne coexistent jamais.
 *
 * COMPORTEMENT.
 *   · Rien de non essentiel ne démarre tant que le visiteur n'a pas tranché
 *     (`IntercomProvider` attend le consentement).
 *   · « Personnaliser » déplie la seule catégorie qui soit un choix, le support
 *     client (Intercom). Les cookies strictement nécessaires sont montrés mais
 *     sans interrupteur : un interrupteur bloqué est un mensonge poli.
 *   · Le choix est versionné (`CONSENT_VERSION`) et effaçable depuis /cookies.
 *
 * OÙ IL EST MONTÉ. Une seule fois, dans `App.tsx`, au-dessus des routes : le
 * bandeau doit apparaître sur la home comme sur /pricing comme sur /agent, et
 * le monter par page est la façon dont on se retrouve avec une route qui ne
 * demande rien.
 */
export function CookieBanner() {
  const { t } = useTranslation('common');
  const [decided, setDecided] = useState<boolean>(() => readConsent() !== null);
  const [customizing, setCustomizing] = useState(false);
  const [support, setSupport] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => subscribeConsent((c) => setDecided(c !== null)), []);

  if (decided) return null;

  const decide = (choice: { support: boolean; analytics: boolean }) => {
    writeConsent(choice);
    setDecided(true);
  };

  return (
    <div
      // `role="dialog"` et non `alertdialog` : le bandeau n'interrompt pas, il
      // propose. Pas de focus-trap pour la même raison — on peut lire la page,
      // y compris la politique de confidentialité, avant de répondre.
      role="dialog"
      aria-modal="false"
      aria-label={t('cookies.banner.title')}
      data-testid="cookie-banner"
      className="fixed bottom-4 right-4 z-[60] w-[min(26rem,calc(100vw-2rem))] rounded-control border border-ink/15 bg-white p-5 shadow-lg"
    >
      <h2 className="font-heading text-[1rem] font-semibold text-ink">
        {t('cookies.banner.title')}
      </h2>
      <p className="mt-2 text-[0.82rem] leading-[1.55] text-ink/70">
        {t('cookies.banner.body')}{' '}
        <Link to="/resources/cookies" className="underline underline-offset-2 hover:text-ink">
          {t('cookies.banner.learnMore')}
        </Link>
      </p>

      {customizing && (
        <div className="mt-4 space-y-3 border-t border-ink/10 pt-4" data-testid="cookie-banner-detail">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[0.82rem] font-medium text-ink">
                {t('cookies.categories.necessary.title')}
              </p>
              <p className="text-[0.75rem] leading-snug text-ink/56">
                {t('cookies.categories.necessary.body')}
              </p>
            </div>
            {/* Pas d'interrupteur : la catégorie n'est pas un choix, et un
                interrupteur grisé le ferait croire. */}
            <span className="shrink-0 rounded-control border border-ink/15 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-ink/55">
              {t('cookies.categories.alwaysOn')}
            </span>
          </div>

          <label className="flex items-start justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-[0.82rem] font-medium text-ink">
                {t('cookies.categories.support.title')}
              </span>
              <span className="block text-[0.75rem] leading-snug text-ink/56">
                {t('cookies.categories.support.body')}
              </span>
            </span>
            <input
              type="checkbox"
              data-testid="cookie-banner-support"
              checked={support}
              onChange={(e) => setSupport(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 accent-ink"
            />
          </label>

          <label className="flex items-start justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-[0.82rem] font-medium text-ink">
                {t('cookies.categories.analytics.title')}
              </span>
              <span className="block text-[0.75rem] leading-snug text-ink/56">
                {t('cookies.categories.analytics.body')}
              </span>
            </span>
            <input
              type="checkbox"
              data-testid="cookie-banner-analytics"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 accent-ink"
            />
          </label>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="cookie-banner-reject"
          onClick={() => decide({ support: false, analytics: false })}
          className="inline-flex min-h-11 items-center justify-center rounded-control border border-ink/20 px-3.5 text-[0.82rem] font-medium text-ink transition-colors hover:bg-ink/5"
        >
          {t('cookies.banner.rejectAll')}
        </button>
        <button
          type="button"
          data-testid="cookie-banner-accept"
          onClick={() => decide({ support: true, analytics: true })}
          className="inline-flex min-h-11 items-center justify-center rounded-control bg-ink px-3.5 text-[0.82rem] font-medium text-white transition-opacity hover:opacity-90"
        >
          {t('cookies.banner.acceptAll')}
        </button>
        {customizing ? (
          <button
            type="button"
            data-testid="cookie-banner-save"
            onClick={() => decide({ support, analytics })}
            className="ml-auto inline-flex min-h-11 items-center justify-center px-2 text-[0.82rem] text-ink/70 underline underline-offset-4 transition-colors hover:text-ink"
          >
            {t('cookies.banner.save')}
          </button>
        ) : (
          <button
            type="button"
            data-testid="cookie-banner-customize"
            onClick={() => setCustomizing(true)}
            className="ml-auto inline-flex min-h-11 items-center justify-center px-2 text-[0.82rem] text-ink/70 underline underline-offset-4 transition-colors hover:text-ink"
          >
            {t('cookies.banner.customize')}
          </button>
        )}
      </div>
    </div>
  );
}
